import { useCallback, useEffect, useState } from 'react'
import {
  SUBMITTED_KEY,
  isFeedbackDismissed,
  isFeedbackSubmitted,
  markFeedbackDismissed,
  markFeedbackSubmitted,
} from '../services/feedbackStorage'

const AUTO_OPEN_DELAY = 30000 // 30s after the user starts scrolling the site

export default function useFeedbackPopup() {
  const [isOpen, setIsOpen] = useState(false)
  const [submitted, setSubmitted] = useState(isFeedbackSubmitted)
  const [dismissed, setDismissed] = useState(isFeedbackDismissed)

  useEffect(() => {
    // Answered for good, or closed earlier in this session: no auto-open.
    if (submitted || dismissed) return

    let timer

    // Only start the 30s countdown after the user has scrolled the site.
    const handleScroll = () => {
      window.removeEventListener('scroll', handleScroll)
      timer = setTimeout(() => setIsOpen(true), AUTO_OPEN_DELAY)
    }

    window.addEventListener('scroll', handleScroll, { passive: true })

    return () => {
      window.removeEventListener('scroll', handleScroll)
      if (timer) clearTimeout(timer)
    }
  }, [submitted, dismissed])

  useEffect(() => {
    // Submitting in one tab hides the popup in the others right away.
    const handleStorage = (event) => {
      if (event.key === SUBMITTED_KEY && event.newValue === 'true') {
        setSubmitted(true)
        setIsOpen(false)
      }
    }

    window.addEventListener('storage', handleStorage)
    return () => window.removeEventListener('storage', handleStorage)
  }, [])

  const open = useCallback(() => setIsOpen(true), [])

  const close = useCallback(() => {
    markFeedbackDismissed()
    setDismissed(true)
    setIsOpen(false)
  }, [])

  const markSubmitted = useCallback(() => {
    markFeedbackSubmitted()
    setSubmitted(true)
    setIsOpen(false)
  }, [])

  return { isOpen, open, close, submitted, markSubmitted }
}
